import { themeColours, type ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import {
  profileImageSpec,
  type ArtistProfileKind,
  type ArtistProfileResponse,
} from 'fym-shared/src/music24/artistProfile';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import type { ProfileRelease } from '../profileReleases';
import { usesReleaseLook, type ProfileToolDraft } from './profileToolDraft';

/** The pictures that the preview of a draft can show. */
export interface ToolPreviewFacts {
  release: ProfileRelease | undefined;
  /** The address of the picked bank or AI picture. */
  pictureUrl: string | undefined;
  /** What the preview shows while the picked source has no picture yet (the current file, else the theme). */
  fallback: HeaderPreviewGround;
}

/**
 * The ground of the live preview of a draft (design b1–b4): the blurred cover of the release, the
 * plain cover of an artist photo, the photo, the bank or AI picture with the focus, or the theme
 * field. A source with no picture yet shows the fallback. Pure.
 */
export function toolGround(
  kind: ArtistProfileKind,
  draft: ProfileToolDraft,
  facts: ToolPreviewFacts
): HeaderPreviewGround {
  const focus = { x: draft.focusX, y: draft.focusY };
  switch (draft.source) {
    case 'cover': {
      const url = facts.release?.artworkUrl;
      if (url === undefined) {
        return facts.fallback;
      }
      return profileImageSpec(kind).coverMode === 'blur'
        ? { kind: 'cover', artworkUrl: url }
        : { kind: 'picture', url, focus };
    }
    case 'photo':
      return draft.photoUrl === undefined ? facts.fallback : { kind: 'picture', url: draft.photoUrl, focus };
    case 'bank':
    case 'ai':
      return facts.pictureUrl === undefined ? facts.fallback : { kind: 'picture', url: facts.pictureUrl, focus };
    case 'theme':
      return { kind: 'theme' };
    default:
      return facts.fallback;
  }
}

/** Where the colours of a draft come from (the "Colours" line of the rail). */
export type ToolColoursFrom = 'artist' | 'release' | 'none';

/**
 * The colours of a draft (spec D3): "Use the look of this release" on: the theme and the colours of
 * the release kit. Off: the theme of the artist look with the cover colours of the release of a
 * cover source, else the colours of the artist look. A plain kind (the artist photo, a press photo)
 * adds no colours. Pure.
 */
export function toolColours(
  kind: ArtistProfileKind,
  draft: ProfileToolDraft,
  look: ArtistProfileResponse['look'],
  release: ProfileRelease | undefined
): { colours: ThemeColours; from: ToolColoursFrom } {
  if (usesReleaseLook(kind, draft) && release?.look !== undefined && release.look !== null) {
    return { colours: themeColours(release.look.themeId, release.palette), from: 'release' };
  }
  const palette =
    draft.source === 'cover' && release !== undefined && release.palette.length > 0 ? release.palette : look.palette;
  const from: ToolColoursFrom = profileImageSpec(kind).coverMode === 'plain' ? 'none' : 'artist';
  return { colours: themeColours(look.themeId, palette), from };
}
